import React, { useEffect, useState } from 'react';
import { string } from 'prop-types';
import { useRive, Layout, Fit, Alignment } from '@rive-app/react-canvas';
import { text } from 'polyglot/polyglot';
import { truncate } from 'lodash';
import { formatCurrency } from 'utils/currency';
import useNumbersDrawnStore from 'stores/numbers-drawn';
import useWinnerStatsStore from 'stores/winner-stats';
import SoundManager from 'utils/SoundManager';
import Scrollbars from 'components/Scrollbars/Scrollbars';

import { getThemeAssetUrl } from 'utils/themeAssets';

import styles from './BingoRiveAnimation.module.scss';

const numbersDrawnSelector = (state) => [state.numbers];
const winnerListSelector = (state) => state.winners;

export default function RiveLoader({ animation }) {
  const [locale, setLocale] = useState(text.locale());
  useEffect(() => {
    setLocale(text.locale());
  }, []);

  const BingoAnimationSv = getThemeAssetUrl('animations/bingo-jackpot-sv.riv');
  const BingoAnimationEn = getThemeAssetUrl('animations/bingo-jackpot-en.riv');

  let BingoAnimation;
  switch (locale) {
    case 'sv':
      BingoAnimation = BingoAnimationSv;
      break;
    case 'en':
      BingoAnimation = BingoAnimationEn;
      break;
    default:
      BingoAnimation = BingoAnimationSv;
  }

  let animationSound = animation;
  switch (animationSound) {
    case 'bingo':
      animationSound = 'bingo';
      break;
    case 'jackpot':
      animationSound = 'jackpot';
      break;
    case 'jackpot-shared':
      animationSound = 'jackpot';
      break;
    default:
      animationSound = 'bingo';
  }

  const animationConfig = {
    src: BingoAnimation,
    stateMachines: animation,
    artboard: animation === 'bingo' ? 'bingo-artboard' : '',
    layout: new Layout({
      fit: Fit.Contain,
      alignment: Alignment.Center,
    }),
    autoplay: true,
  };
  const { rive, RiveComponent } = useRive({
    ...animationConfig,
    onLoadError: (err) => {
      console.log(`error loading rive animation: ${animation}`, err);
    },
    onPlay: () => {
      SoundManager.instance.playVO(animationSound);
    },
    onStop: () => {
      SoundManager.instance.stopVO(animationSound);
    },
  });

  const [numbersDrawn] = useNumbersDrawnStore(numbersDrawnSelector);
  const [finalNumbers, setFinalNumbers] = useState([]);
  const winnerList = useWinnerStatsStore(winnerListSelector);

  useEffect(() => {
    const numbersDrawnAsc = [...numbersDrawn].reverse();

    while (numbersDrawnAsc.length < 5) {
      const randomNum = Math.floor(Math.random() * 75) + 1;
      if (!numbersDrawnAsc.includes(randomNum)) {
        numbersDrawnAsc.push(randomNum);
      }
    }

    setFinalNumbers(numbersDrawnAsc);
  }, [numbersDrawn]);

  useEffect(() => {
    if (rive && animation === 'bingo') {
      rive.setTextRunValue('darkblue-ball-text', finalNumbers[0].toString());
      rive.setTextRunValue('pink-ball-text', finalNumbers[1].toString());
      rive.setTextRunValue('green-ball-text', finalNumbers[2].toString());
      rive.setTextRunValue('lightblue-ball-text', finalNumbers[3].toString());
      rive.setTextRunValue('orange-ball-text', finalNumbers[4].toString());
    }
  }, [rive, animation, finalNumbers]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <RiveComponent />;
      </div>

      <div className={styles.text}>
        <h3 className={styles.title}>
          {text.t('prizeNotification.bingo.title')}
        </h3>

        <div className={styles.list}>
          <Scrollbars className={styles.scroll}>
            {winnerList.map((winner, index) => (
              <div
                className={styles.item}
                // eslint-disable-next-line react/no-array-index-key
                key={winner.name + winner.amount + index}
              >
                <p className={styles.name}>
                  {truncate(winner.name, { length: 16 })}
                </p>
                <p className={styles.sum}>{formatCurrency(winner.amount)}</p>
              </div>
            ))}
          </Scrollbars>
        </div>
      </div>
    </div>
  );
}

RiveLoader.propTypes = {
  animation: string,
};

RiveLoader.defaultProps = {
  animation: 'bingo',
};
